/* ==========================================================================
   Aurelie Silks - v4 behaviour
   Shared by the Shopify theme and the static preview.
   Everything degrades: with JS off the page is fully readable, and the form
   still posts a real variant through the noscript select.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };

  function money(cents) {
    var n = cents / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* ---------------------------------------------------------------- reveals */
  function initReveals() {
    var nodes = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (!nodes.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    function show(el, io) {
      var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
      el.style.setProperty('--reveal-delay', delay + 'ms');
      el.classList.add('is-in');
      if (io) io.unobserve(el);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) show(entry.target, io);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

    nodes.forEach(function (n) { io.observe(n); });

    /* Anything already in the first screenful reveals straight away. */
    window.requestAnimationFrame(function () {
      nodes.forEach(function (n) {
        if (n.getBoundingClientRect().top < window.innerHeight * 0.92) show(n, io);
      });
    });
  }

  /* ---------------------------------------------------------------- header  */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    var progress = document.querySelector('[data-progress]');

    if (header || progress) {
      var ticking = false;
      var update = function () {
        var y = window.scrollY || window.pageYOffset;
        if (header) header.classList.toggle('is-stuck', y > 8);
        if (progress) {
          var h = document.documentElement.scrollHeight - window.innerHeight;
          progress.style.transform = 'scaleX(' + (h > 0 ? Math.min(1, Math.max(0, y / h)) : 0).toFixed(4) + ')';
        }
        ticking = false;
      };
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }, { passive: true });
      update();
    }

    var toggle = document.querySelector('[data-nav-toggle]');
    var panel = document.querySelector('[data-nav-panel]');
    if (!toggle || !panel) return;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
    }
    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });
    each(panel.querySelectorAll('a'), function (a) {
      a.addEventListener('click', function () { setOpen(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });
  }

  /* ---------------------------------------------------------------- faq     */
  function initFaq() {
    each(document.querySelectorAll('.faq__item'), function (item) {
      var q = item.querySelector('[data-faq-q]');
      item.classList.toggle('is-open', !!q && q.getAttribute('aria-expanded') === 'true');
    });
    each(document.querySelectorAll('[data-faq-q]'), function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        var group = btn.closest('[data-faq]');
        if (group && group.getAttribute('data-faq-single') === 'true' && !open) {
          each(group.querySelectorAll('[data-faq-q]'), function (other) {
            other.setAttribute('aria-expanded', 'false');
          });
        }
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (group) {
          each(group.querySelectorAll('.faq__item'), function (item) {
            var q = item.querySelector('[data-faq-q]');
            item.classList.toggle('is-open', !!q && q.getAttribute('aria-expanded') === 'true');
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------- colourways */
  /* One source of truth for the chosen colour. The swatches, the colourway
     stage, the page accent and the buy form all read from it. */
  var Colour = {
    current: null,
    listeners: [],
    set: function (key, hex, label) {
      if (this.current === key) return;
      this.current = key;
      if (hex) document.documentElement.style.setProperty('--accent', hex);
      var self = this;
      this.listeners.forEach(function (fn) { fn(key, hex, label, self); });
    },
    on: function (fn) { this.listeners.push(fn); }
  };

  function initColourways() {
    var swatches = document.querySelectorAll('[data-swatch]');
    if (!swatches.length) return;

    each(swatches, function (btn) {
      btn.addEventListener('click', function () {
        Colour.set(btn.getAttribute('data-swatch'), btn.getAttribute('data-hex'), btn.getAttribute('data-label'));
      });
    });

    Colour.on(function (key, hex, label) {
      each(document.querySelectorAll('[data-swatch]'), function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-swatch') === key ? 'true' : 'false');
      });
      each(document.querySelectorAll('[data-colour-layer]'), function (layer) {
        layer.classList.toggle('is-active', layer.getAttribute('data-colour-layer') === key);
      });
      if (label) {
        each(document.querySelectorAll('[data-colour-name]'), function (el) { el.textContent = label; });
      }
    });

    var seed = document.querySelector('[data-swatch][aria-pressed="true"]') || swatches[0];
    if (seed) {
      Colour.current = null;
      Colour.set(seed.getAttribute('data-swatch'), seed.getAttribute('data-hex'), seed.getAttribute('data-label'));
    }
  }

  /* ------------------------------------------------------------- buy block  */
  function initBuy() {
    var root = document.querySelector('[data-buy-form]');
    if (!root) return;

    var idInput = root.querySelector('[data-variant-id]');
    var submit = root.querySelector('[data-buy-submit]');
    var priceEl = document.querySelector('[data-buy-price]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var addLabel = root.getAttribute('data-add-label') || 'Add to cart';
    var soldOutLabel = root.getAttribute('data-sold-out-label') || 'Sold out';

    var variants = [];
    var dataEl = document.querySelector('[data-variant-json]');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent) || []; } catch (e) { variants = []; }
    }

    var radios = Array.prototype.slice.call(root.querySelectorAll('input[data-tier]'));
    var state = { colour: null, sets: null };

    function match(colour, sets) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.colour && colour && v.colour !== colour) continue;
        if (v.sets && sets && v.sets !== sets) continue;
        return v;
      }
      return null;
    }

    function singleBoxPrice() {
      for (var i = 0; i < radios.length; i++) {
        if (parseInt(radios[i].getAttribute('data-boxes'), 10) === 1) {
          var v = match(state.colour, radios[i].getAttribute('data-tier'));
          if (v) return v.price;
        }
      }
      return null;
    }

    function render() {
      /* Row states, prices and savings. */
      var single = singleBoxPrice();
      radios.forEach(function (radio) {
        var label = radio.closest('.tier');
        if (label) label.classList.toggle('is-checked', radio.checked);

        var v = match(state.colour, radio.getAttribute('data-tier'));
        var priceSlot = label && label.querySelector('[data-tier-price]');
        var saveSlot = label && label.querySelector('[data-tier-save]');
        if (v && priceSlot) priceSlot.textContent = money(v.price);
        if (v && saveSlot) {
          var boxes = parseInt(radio.getAttribute('data-boxes'), 10) || 1;
          var saved = single && boxes > 1 ? single * boxes - v.price : 0;
          saveSlot.textContent = saved > 0 ? 'Save ' + money(saved) : '';
        }
      });

      /* The selected variant. */
      var chosen = match(state.colour, state.sets);
      if (!chosen) return;
      if (idInput) idInput.value = chosen.id;
      if (priceEl) priceEl.textContent = money(chosen.price);
      if (stickyPrice) stickyPrice.textContent = money(chosen.price);
      if (submit) {
        var out = chosen.available === false;
        submit.disabled = out;
        submit.textContent = out ? soldOutLabel : addLabel;
      }
    }

    radios.forEach(function (radio) {
      if (radio.checked) state.sets = radio.getAttribute('data-tier');
      radio.addEventListener('change', function () {
        state.sets = radio.getAttribute('data-tier');
        render();
      });
    });

    Colour.on(function (key) { state.colour = key; render(); });
    if (Colour.current) state.colour = Colour.current;

    render();
  }

  /* ------------------------------------------------------------- sticky bar */
  function initStickyBar() {
    var bar = document.querySelector('[data-sticky-buy]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var after = document.querySelector('[data-sticky-after]');
    var hide = document.querySelector('[data-sticky-hide]');
    var pastHero = !after;
    var inBuy = false;

    function sync() { bar.classList.toggle('is-visible', pastHero && !inBuy); }

    if (after) {
      new IntersectionObserver(function (entries) {
        pastHero = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        sync();
      }, { threshold: 0 }).observe(after);
    }
    if (hide) {
      new IntersectionObserver(function (entries) {
        inBuy = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.06 }).observe(hide);
    }
    sync();
  }

  /* ------------------------------------------------------------- parallax   */
  function initParallax() {
    if (reduced) return;
    var nodes = document.querySelectorAll('[data-parallax]');
    if (!nodes.length || !('IntersectionObserver' in window)) return;

    var active = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var i = active.indexOf(e.target);
        if (e.isIntersecting && i === -1) active.push(e.target);
        if (!e.isIntersecting && i > -1) active.splice(i, 1);
      });
    }, { rootMargin: '25% 0px' });
    each(nodes, function (n) { io.observe(n); });

    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      active.forEach(function (el) {
        var strength = parseFloat(el.getAttribute('data-parallax')) || 0.1;
        var r = el.parentElement.getBoundingClientRect();
        var offset = (r.top + r.height / 2 - vh / 2) * -strength;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ------------------------------------------------------- hero ad variant  */
  /* ?v=ritual serves the benefit led message, so benefit led ads land on
     matching words instead of the gift message. */
  function initHeroVariant() {
    var hero = document.querySelector('[data-hero-variants]');
    if (!hero) return;
    var param = null;
    try { param = new URLSearchParams(window.location.search).get('v'); } catch (e) { param = null; }
    var key = param === 'ritual' ? 'ritual' : 'gift';
    each(hero.querySelectorAll('[data-hero-variant]'), function (el) {
      el.hidden = el.getAttribute('data-hero-variant') !== key;
    });
  }

  /* ------------------------------------------------------------- boot       */
  function boot() {
    initHeroVariant();
    initHeader();
    initColourways();
    initBuy();
    initFaq();
    initStickyBar();
    initParallax();
    initReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
